import { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Share2, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { api, getUser } from "../api";

// Componente unico e riutilizzabile di condivisione: un solo pulsante "Condividi" che apre un popover.
// Il popover è renderizzato in un portal (position: fixed) ancorato al bottone con posizionamento smart
// (apre sotto se c'è spazio, altrimenti sopra; clamp nel viewport) => nessuno scroll anomalo né layout shift.
// - youpam: { entityType, entityId, orgId } → mostra la sezione "Su YouPAM" solo al proprietario (struttura).
// - Sempre: condivisione esterna (Web Share nativa se disponibile, WhatsApp, Facebook, X, LinkedIn, Telegram, Copia link).
const PANEL_W = 224; // w-56

export function ShareMenu({ shareUrl, title, text, youpam, className = "", onShared }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      const pw = panelRef.current?.offsetWidth || PANEL_W;
      const ph = panelRef.current?.offsetHeight || 300;
      const m = 8, vw = window.innerWidth, vh = window.innerHeight;
      let left = Math.min(Math.max(m, b.right - pw), vw - pw - m);
      let top = b.bottom + 6;
      if (top + ph > vh - m && b.top - ph - 6 >= m) top = b.top - ph - 6; // flip sopra
      top = Math.max(m, Math.min(top, vh - ph - m));
      setCoords({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  const u = getUser();
  const canYoupam = youpam && u && u.id === youpam.orgId;
  const url = shareUrl;
  const enc = encodeURIComponent;
  const caption = text || title || "";
  const msg = [caption, url].filter(Boolean).join(" ");
  const ext = [
    { key: "whatsapp", href: `https://wa.me/?text=${enc(msg)}`, label: "WhatsApp" },
    { key: "facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, label: "Facebook" },
    { key: "x", href: `https://twitter.com/intent/tweet?text=${enc(caption)}&url=${enc(url)}`, label: "X" },
    { key: "linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, label: "LinkedIn" },
    { key: "telegram", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(caption)}`, label: "Telegram" },
  ];
  const close = () => { setOpen(false); setCoords(null); };
  const openExt = (href) => { window.open(href, "_blank", "noopener,noreferrer"); close(); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success(t("sharex.copied")); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error(t("sharex.copy_failed")); }
    close();
  };
  const native = async () => {
    try { await navigator.share({ title, text: caption, url }); }
    catch (e) { if (e?.name !== "AbortError") copy(); }
    close();
  };
  const shareYoupam = async () => {
    try {
      await api.post("/posts", { body: "", entity_type: youpam.entityType, entity_id: youpam.entityId });
      toast.success(t("sharex.shared_ok")); onShared?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("sharex.share_failed")); }
    close();
  };
  const hasNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className="inline-block" data-testid="share-menu">
      <button type="button" ref={btnRef} className={`yp-btn yp-btn-outline !py-1.5 !px-3 text-sm inline-flex items-center gap-1 ${className}`}
        onClick={() => (open ? close() : setOpen(true))} data-testid="share-menu-btn" aria-haspopup="true" aria-expanded={open}>
        <Share2 size={16} /> {t("sharex.share")}
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={close} data-testid="share-menu-backdrop" />
          <div ref={panelRef} role="menu" data-testid="share-menu-panel"
            style={{ position: "fixed", top: coords ? coords.top : -9999, left: coords ? coords.left : -9999, opacity: coords ? 1 : 0 }}
            className="z-[9999] w-56 bg-white border border-slate-200 rounded-xl shadow-lg p-2 text-sm">
            {canYoupam && (<div className="mb-1">
              <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-400">YouPAM</div>
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 font-medium text-blue-600" onClick={shareYoupam} data-testid="share-on-youpam">{t("sharex.on_youpam")}</button>
              <div className="my-1 border-t border-slate-100" />
            </div>)}
            <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-400">{t("sharex.others")}</div>
            {hasNative && <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50" onClick={native} data-testid="share-native">{t("sharex.native")}</button>}
            {ext.map(o => <button key={o.key} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50" onClick={() => openExt(o.href)} data-testid={`share-${o.key}`}>{o.label}</button>)}
            <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 inline-flex items-center gap-2" onClick={copy} data-testid="share-copy">{copied ? <Check size={14} /> : <LinkIcon size={14} />} {t("sharex.copy")}</button>
          </div>
        </>, document.body)}
    </div>);
}
